import React, { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { StudentPicker } from "./StudentPicker";
import { GroupForm, emptyGroup, groupBody } from "./GroupForm";

const GroupCard = ({ g, i, students, onEdit, onRemove }) => {
  const names = students.filter((s) => g.student_ids.includes(s.id)).map((s) => s.name);
  return (
    <div className="flex items-start justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid={`group-card-${i}`}>
      <div className="min-w-0 text-sm">
        <p className="font-bold text-emerald-900">Group {i + 1} · {names.length} student(s)</p>
        <p className="truncate text-xs text-slate-600">{names.join(", ")}</p>
        <p className="mt-1 text-xs text-slate-700">{[g.topic, g.homework && `HW: ${g.homework}`, `${g.activities.length} activities`].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={onEdit} className="p-1 text-slate-500 hover:text-slate-900" aria-label="Edit group" data-testid={`group-edit-${i}`}><Pencil className="h-4 w-4" /></button>
        <button type="button" onClick={onRemove} className="p-1 text-slate-500 hover:text-red-600" aria-label="Remove group" data-testid={`group-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const WholeClassPlanner = ({ date, period, students, planned, meta, onSaved, onCancel, onChipsChanged, onSchoolSet }) => {
  const [groups, setGroups] = useState([]);
  const [g, setG] = useState(emptyGroup());
  const [at, setAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = groups.length > 0 || g.student_ids.length > 0;
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const taken = new Set([...planned, ...groups.flatMap((x) => x.student_ids)]);
  const left = students.filter((s) => !taken.has(s.id)).length;
  const addGroup = () => {
    if (!g.student_ids.length) { toast.error("Pick students for this group"); return; }
    if (!g.activities.length && !g.topic && !g.homework && !g.chips.length) { toast.error("Tick an activity or write the topic"); return; }
    setGroups((x) => (at === null ? [...x, g] : [...x.slice(0, at), g, ...x.slice(at)]));
    setAt(null);
    setG(emptyGroup());
  };
  const edit = (i) => { if (g.student_ids.length) { toast.error("Add or clear the current group first"); return; } setG(groups[i]); setAt(i); setGroups((x) => x.filter((_, j) => j !== i)); };
  const saveAll = async () => {
    const all = g.student_ids.length ? [...groups, g] : groups;
    if (!all.length) { toast.error("Add at least one group"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/teacher/diary/plans/bulk", { period, date, groups: all.map(groupBody) });
      toast.success(`Saved ${data.saved} plan(s) for the class`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save plans"); } finally { setBusy(false); }
  };
  const cancel = () => { if (!dirty || window.confirm(`You have ${groups.length} unsaved group(s). Leave without saving?`)) onCancel(); };
  return (
    <div className="space-y-4 rounded-2xl border-2 border-emerald-200 bg-white p-4" data-testid="class-planner">
      <p className={`rounded-lg px-3 py-2 text-sm font-bold ${left ? "bg-amber-50 text-amber-900" : "bg-emerald-100 text-emerald-900"}`} data-testid="planner-counter">
        {students.length - left} of {students.length} students planned{left ? ` · ${left} left` : " · everyone is covered"}
      </p>
      {groups.map((x, i) => <GroupCard key={i} g={x} i={i} students={students} onEdit={() => edit(i)} onRemove={() => setGroups((y) => y.filter((_, j) => j !== i))} />)}
      <section className="space-y-3 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-bold text-slate-800">Group {at === null ? groups.length + 1 : at + 1} · who?</p>
        <StudentPicker students={students} value={g.student_ids} onChange={(ids) => setG({ ...g, student_ids: ids })} onSchoolSet={onSchoolSet} taken={taken} />
        <GroupForm g={g} set={setG} students={students} meta={meta} onChipsChanged={onChipsChanged} />
        <Button variant="outline" onClick={addGroup} data-testid="planner-add-group"><Plus className="mr-1 h-4 w-4" /> Add group</Button>
      </section>
      <div className="flex gap-2">
        <Button onClick={saveAll} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="planner-save-all">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save all plans</Button>
        <Button variant="outline" onClick={cancel} data-testid="planner-cancel">Cancel</Button>
      </div>
    </div>
  );
};
